"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NiftoryClient = void 0;
const graphql_1 = require("../../generated/graphql");
const authClients_1 = require("../authClients");
const baseNiftoryClient_1 = require("./baseNiftoryClient");
// polyfill to support urql in node <18
require("isomorphic-unfetch");
class NiftoryClient extends baseNiftoryClient_1.BaseNiftoryClient {
    /**@hidden */
    constructor(params) {
        super(params);
        const { appId, authToken, apiKey, clientSecret } = this.params;
        if (clientSecret) {
            const authClient = new authClients_1.ClientCredentialsAuthClient({
                appId,
                apiKey,
                clientSecret,
                environment: this.environment,
            });
            this.graphqlClient = authClient.getGraphqlClient();
            return;
        }
        const authClient = new authClients_1.TokenAuthClient({
            appId,
            apiKey,
            authToken,
            environment: this.environment,
        });
        this.graphqlClient = authClient.getGraphqlClient();
    }
    /**
     * Gets the current admin user using the Niftory API
     * @category User
     * @returns the current admin user
     */
    async getAdminUser() {
        const response = await this.executeQuery(graphql_1.AdminUserDocument);
        return response.adminUser;
    }
    /**
     * Gets a file by ID using the Niftory API.
     * @category File
     * @param id The ID of the file to retrieve
     * @returns the file
     */
    async getFile(id) {
        const response = await this.executeQuery(graphql_1.FileDocument, { id });
        return response.file;
    }
    /**
     * Gets an NFT by ID using the Niftory API.
     * @category Nft
     * @param id The ID of the NFT to retrieve
     * @returns the NFT
     */
    async getNft(id) {
        const response = await this.executeQuery(graphql_1.NftDocument, { id });
        return response.nft;
    }
    /**
     * Gets NFTs associated with the current AppUser context, including those that are transferring or failed to transfer. Read more [here](https://docs.niftory.com/home/v/api/core-concepts/nfts/querying-nfts).
     * @category Nft
     * @param params.cursor Cursor for pagination
     * @param params.filter Params to filter nfts by
     * @param params.maxResults Max results to return
     * @param params.userId ID of the user
     * @param params.appId ID of the app, only used for admin users
     * @returns The list of NFTs
     */
    async getNfts(params) {
        var _a, _b;
        const response = await this.executeQuery(graphql_1.NftsDocument, { ...params, appId: (_b = (_a = this.params) === null || _a === void 0 ? void 0 : _a.appId) !== null && _b !== void 0 ? _b : this.appId });
        return response.nfts;
    }
    /**
     * Gets NFTs associated with the current wallet, including those that are transferring or failed to transfer. Read more [here](https://docs.niftory.com/home/v/api/core-concepts/nfts/querying-nfts).
     * @category Nft
     * @param params.address Address of the wallet
     * @param params.filter Params to filter wallet
     * @param params.maxResults Max results to return
     * @param params.walletId ID of the wallet
     * @returns The list of NFTs
     */
    async getNftsByWallet(params) {
        const { address, cursor, filter, maxResults, walletId } = params;
        const response = await this.executeQuery(graphql_1.NftsByWalletDocument, { address, cursor, filter, maxResults, walletId });
        return response.nftsByWallet;
    }
    /**
     * Initiates the transfer of an NFT to the currently-logged in AppUser. The NFT is reserved for the user in database, and you can use the NFT.status field to check on the transfer progress.
     * @category Nft
     * @param params.nftId the ID of the NFT to transfer
     * @param params.nftModelId the ID of the NFT model
     * @param params.userId the ID of the user to transfer to
     * @param params.walletId the ID of the wallet to transfer to
     * @param params.address the address of the wallet to transfer to
     * @param params.force whether to force the transfer
     * @param params.appId the ID of the app - only required when calling with admin privileges
     * @returns the NFT which was transferred
     */
    async transfer(params) {
        var _a;
        const result = await this.executeMutation(graphql_1.TransferDocument, {
            ...params,
            appId: (_a = params === null || params === void 0 ? void 0 : params.appId) !== null && _a !== void 0 ? _a : this.appId,
        }, ["Nft"]);
        return result.transfer;
    }
    /**
     * Withdraws an NFT from a Niftory Custodial Wallet to an external wallet address.
     * @category Nft
     * @param params.id the ID of the NFT to withdraw
     * @param params.niftoryWalletAddress the address of the wallet to withdraw from
     * @param params.receiverAddress the address of the wallet to withdraw to
     * @param params.appId the ID of the app - only required when calling with admin privileges
     * @returns the NFT which was withdrawn
     */
    async withdraw(params) {
        var _a;
        const { id, niftoryWalletAddress, receiverAddress } = params;
        const response = await this.executeMutation(graphql_1.WithdrawDocument, {
            ...params,
            appId: (_a = params === null || params === void 0 ? void 0 : params.appId) !== null && _a !== void 0 ? _a : this.appId,
        }, ["Nft"]);
        return response.withdraw;
    }
    /**
     * Gets a set by ID using the Niftory API
     * @category Set
     * @param id the ID of the set to retrieve
     * @returns the set
     */
    async getSet(id) {
        const response = await this.executeQuery(graphql_1.SetDocument, { id });
        return response.set;
    }
    /**
     * Gets a list of sets using the Niftory API
     * @category Set
     * @param params.filter.title the title of the set
     * @param params.filter.ids the IDs of the sets
     * @param params.filter.tags the tags of the sets
     * @param params.filter.blockchainIds the blockchain IDs of the sets
     * @param params.appId the ID of the app - only required when calling with admin privileges
     * @returns the list of sets
     */
    async getSets(params) {
        var _a;
        const response = await this.executeQuery(graphql_1.SetsDocument, {
            ...params,
            appId: (_a = params === null || params === void 0 ? void 0 : params.appId) !== null && _a !== void 0 ? _a : this.appId,
        });
        return response.sets;
    }
    /**
     * Gets an NFT Model by ID using the Niftory API.
     * @category NftSet
     * @param params.appId The ID of the app - only required when calling with admin privileges
     * @param params.cursor The cursor to use for pagination
     * @param params.maxResults The maximum number of results to return per page
     * @param params.filter The filter to apply to the results for the nftSets query
     * @param
     * @returns the NFT Set
     */
    async getNftSets(params) {
        var _a;
        const response = await this.executeQuery(graphql_1.NftModelsDocument, { ...params, appId: (_a = params === null || params === void 0 ? void 0 : params.appId) !== null && _a !== void 0 ? _a : this.appId });
        return response.nftSets;
    }
    /**
     * Creates a new set using the Niftory API
     * @category Set
     * @param params.title the title of the set
     * @param params.attributes the attributes of the set as a json, these will not be added to the blockchain
     * @param params.tags the tags of the set, these will be stored in the API as well as the blockchain.
     * @returns the newly created set
     */
    async createSet(params) {
        var _a;
        const response = await this.executeMutation(graphql_1.CreateNftSetDocument, {
            data: params.data,
            appId: (_a = params === null || params === void 0 ? void 0 : params.appId) !== null && _a !== void 0 ? _a : this.appId,
        }, ["NftSet", "NftModel"]);
        return response.createNFTSet;
    }
    /**
     * Deletes an existing NFTSet. This operation will only be perfomed if no NFTs have been minted from this NFTSet
     * @category NftListing
     * @param id ID of the NFTSet to delete
     * @returns the deleted NFTSet
     */
    async deleteNftSet(id) {
        const response = await this.executeMutation(graphql_1.DeleteNftSetDocument, {
            id,
        }, ["NftSet", "NftModel"]);
        return response.deleteNFTSet;
    }
    /**
     * Updates an existing NftSet.
     * @category NftSet
     * @param params.id ID of the NFTSet to update
     * @param params.data Data to update NFTSet
     * @returns the updated NftSet
     */
    async updateNftSet(params) {
        const response = await this.executeMutation(graphql_1.UpdateNftSetDocument, params, ["NftSet", "NftModel"]);
        return response.updateNFTSet;
    }
    /**
     * Creates a new NFTModel using the Niftory API
     * @category NftModel
     * @param params.setId the ID of the set to add the NFTModel to
     * @param params.title the title of the new NFTModel
     * @param params.description the description of the new NFTModel
     * @param params.quantity the quantity of NFTs to mint. This should at least be 1
     * @param params.fileId the ID of the file to use as the content of the NFTModel
     * @param params.tags the tags of the NFTModel
     *
     * @returns the newly created NFTModel
     */
    async createNftModel(params) {
        const response = await this.executeMutation(graphql_1.CreateNftModelDocument, params, ["NftModel"]);
        return response.createNFTModel;
    }
    /**
     * Deletes an existing NFTModel. This operation will only be perfomed if no NFTs have been minted from this NFTModel
     * @category NftModel
     * @param id ID of the NFTModel to delete
     * @returns the deleted NFTModel
     */
    async deleteNftModel(id) {
        const response = await this.executeMutation(graphql_1.DeleteNftModelDocument, {
            id,
        }, ["NftModel"]);
        return response.deleteNFTModel;
    }
    /**
     * Updates an existing NFTModel. Note that if this NFTModel has already been used to mint an NFT, the update operation will fail for any properties that affect the blockchain (such as 'quantity', 'title', 'metadata', etc.), whereas updating 'attributes' will succeed.
     * @category NftModel
     * @param params.id ID of the NFTModel to update
     * @param params.data Data to update NftModel
     * @returns the updated NFTModel
     */
    async updateNftModel(params) {
        const response = await this.executeMutation(graphql_1.UpdateNftModelDocument, params, ["NftModel"]);
        return response.updateNFTModel;
    }
    /**
     * Mints an NFT model using the API
     * @category NftModel
     * @param params.id the ID of the NFT model to mint
     * @param params.quantity the quantity of NFTs to mint
     * @param params.appId the ID of the app - only required when calling with admin privileges
     * @returns the NFTModel which was minted
     */
    async mintNftModel(params) {
        var _a;
        const response = await this.executeMutation(graphql_1.MintNftModelDocument, {
            ...params,
            appId: (_a = params === null || params === void 0 ? void 0 : params.appId) !== null && _a !== void 0 ? _a : this.appId,
        }, ["NftModel", "NftSet"]);
        return response.mintNFTModel;
    }
    /**
     * Gets an NFT Model by ID using the Niftory API.
     * @category NftModel
     * @param id The ID of the NFT Model to retrieve
     * @returns the NFT Model
     */
    async getNftModel(id) {
        const response = await this.executeQuery(graphql_1.NftModelDocument, { id });
        return response.nftModel;
    }
    /**
     * Gets an NFT Model by ID using the Niftory API.
     * @category NftModel
     * @param params.appId The ID of the app - only required when calling with admin privileges
     * @param params.cursor The cursor to use for pagination
     * @param params.maxResults The maximum number of results to return per page
     * @param params.filter The filter to apply to the results for the nftModels query
     * @param
     * @returns the NFT Model
     */
    async getNftModels(params) {
        var _a;
        const response = await this.executeQuery(graphql_1.NftModelsDocument, { ...params, appId: (_a = params === null || params === void 0 ? void 0 : params.appId) !== null && _a !== void 0 ? _a : this.appId });
        return response.nftModels;
    }
    /**
     * Deletes an existing NFTListing
     * @category NftListing
     * @param id ID of the NFTListing to delete
     * @returns the deleted NFTListing
     */
    async deleteNftListing(id) {
        const response = await this.executeMutation(graphql_1.DeleteNftListingDocument, {
            deleteNftListingId: id,
        }, ["NftListing", "NftModel"]);
        return response.deleteNFTListing;
    }
    /**
     * Creates a new Niftory Wallet using the Niftory API
     * @category Wallet
     * @param arg.userId The ID of the user to create the wallet for
     * @returns the newly created Niftory Wallet
     */
    async createNiftoryWallet(args) {
        var _a;
        const response = await this.executeMutation(graphql_1.CreateNiftoryWalletDocument, {
            ...args,
            appId: (_a = args === null || args === void 0 ? void 0 : args.appId) !== null && _a !== void 0 ? _a : this.appId,
        }, ["Wallet"]);
        return response.createNiftoryWallet;
    }
    /**
     *
     * @param id The ID of the Niftory Wallet to retrieve
     * @category Wallet
     * @returns the Niftory Wallet
     */
    async getNiftoryWallet(id) {
        const response = await this.executeQuery(graphql_1.WalletByIdDocument, { walletById: id });
        return response.walletById;
    }
    /**
     * Gets the Niftory Wallet of the currently signed-in user
     * @category File
     * @param params.name The name of the file
     * @param params.contentType The content type of the file(video, image, etc.)
     * @param param.description The description of the file
     * @param param.appId The ID of the app - only required when calling with admin privileges
     * @returns the created file
     */
    async createFileUploadUrl(params) {
        var _a;
        const response = await this.executeMutation(graphql_1.CreateFileUploadUrlDocument, {
            ...params,
            appId: (_a = params === null || params === void 0 ? void 0 : params.appId) !== null && _a !== void 0 ? _a : this.appId,
        });
        return response.createFileUploadUrl;
    }
    /**
     *
     * Gets the primary wallet of the current user
     * @category Wallet
     * @returns the Wallet
     */
    async getWallet() {
        const response = await this.executeQuery(graphql_1.WalletDocument);
        return response.wallet;
    }
    /**
     * @param params.address The address of the Wallet to retrieve
     * @param params.appId The ID of the app - only required when calling with admin privileges
     * @category Wallet
     * @returns the Wallet
     */
    async getWalletByAddress(params) {
        var _a;
        const response = await this.executeQuery(graphql_1.WalletByAddressDocument, {
            ...params,
            appId: (_a = params === null || params === void 0 ? void 0 : params.appId) !== null && _a !== void 0 ? _a : this.appId,
        });
        return response.walletByAddress;
    }
    /**
     * @category Wallet
     * @returns all Wallets for the current app
     */
    async getWallets(params) {
        var _a;
        const response = await this.executeQuery(graphql_1.WalletsDocument, {
            ...params,
            appId: (_a = params === null || params === void 0 ? void 0 : params.appId) !== null && _a !== void 0 ? _a : this.appId,
        });
        return response.wallets;
    }
    /**
     * Registers a Wallet, associating it with the currently signed-in AppUser if specified. Read more [here](https://docs.niftory.com/home/v/api/core-concepts/wallets/set-up-wallets).
     * @category Wallet
     * @param address The address of the wallet
     */
    async registerWallet(address) {
        const response = await this.executeMutation(graphql_1.RegisterWalletDocument, {
            address,
        });
        return response.registerWallet;
    }
    /**
     * Verifies a Wallet to the currently signed-in user. If the signed verification code fails to decode with the wallet's public key or doesn't match the wallet's verification code, the request will fail. Read more [here](https://docs.niftory.com/home/v/api/core-concepts/wallets/set-up-wallets).
     * @category Wallet
     * @param params.address The address of the wallet to verify
     * @param params.signedVerificationCode The verification code of the wallet signed by the wallet's private key.
     */
    async verifyWallet(params) {
        const { address, signedVerificationCode } = params;
        const response = await this.executeMutation(graphql_1.VerifyWalletDocument, {
            address,
            signedVerificationCode,
        });
        return response.verifyWallet;
    }
    /**
     * Marks a Wallet as ready, indicating that the wallet is ready to receive NFTs from the app's Contract. The wallet must be verified before this succeeds. Read more [here](https://docs.niftory.com/home/v/api/core-concepts/wallets/set-up-wallets).
     * @category Wallet
     * @param address The address of the wallet
     */
    async readyWallet(address) {
        const response = await this.executeMutation(graphql_1.ReadyWalletDocument, {
            address,
        }, ["Wallet"]);
        return response.readyWallet;
    }
    /**
     * Sets the primary Wallet for the currently signed in user.
     * @category Wallet
     * @param params.address The address of the wallet
     * @param params.walletId The wallet ID of the wallet
     */
    async setPrimaryWallet(params) {
        const response = await this.executeMutation(graphql_1.SetPrimaryWalletDocument, params, ["Wallet"]);
        return response.setPrimaryWallet;
    }
    /**
     * Creates an Organization
     * @category Organization
     * @param params.data.name The name of the organization
     * @param params.data.adminUserEmail A user email to add to the organization.
     * @returns The organization created
     */
    async createOrganization(params) {
        const response = await this.executeMutation(graphql_1.CreateOrganizationDocument, params, ["Organization"]);
        return response.createOrganization;
    }
    /**
     * Creates App
     * @category App
     * @param params.name The name of the app
     * @param params.organizationId The id of the organization where we create the app
     * @param params.blockchain The name of the organization
     * @param params.redirectUris The redirect URIs for the app. Only required for oauth
     * @returns The App created
     */
    async createApp(params) {
        const response = await this.executeMutation(graphql_1.CreateAppDocument, params, ["App"]);
        return response.createApp;
    }
    /**
     * Adds a credential to an app
     * @category App
     * @param params.appId The ID of the app to create a credential for
     * @param params.data The data for the update
     * @param params.type The type of credential to create
     * @returns The added credential
     */
    async addCredential(params) {
        const response = await this.executeMutation(graphql_1.AddCredentialDocument, params, ["Keys"]);
        return response.addCredential;
    }
    /**
     * Updates a credential
     * @category App
     * @param params.clientId The ID of the credential to update
     * @param params.data The data for the update
     * @returns The added credential
     */
    async updateCredential(params) {
        const response = await this.executeMutation(graphql_1.AddCredentialDocument, params, ["Keys"]);
        return response.updateCredential;
    }
    /**
     * Deletes a credential
     * @category App
     * @param params.clientId The ID of the credential to delete
     * @returns The deleted credential, if any
     */
    async deleteCredential(params) {
        const response = await this.executeMutation(graphql_1.AddCredentialDocument, params, ["Keys"]);
        return response.deleteCredential;
    }
    /**
     * Gets the current app
     * @category App
     * @returns The App
     */
    async getCurrentApp() {
        const response = await this.executeQuery(graphql_1.AppDocument);
        return response.app;
    }
    /**
     * Gets app by its ID
     * @category App
     * @param id Id of the app to fetch
     * @returns The App
     */
    async getAppById(id) {
        const response = await this.executeQuery(graphql_1.AppByIdDocument, {
            id,
        });
        return response.appById;
    }
    /**
     * Gets the contract belonging to current app
     * @category Contract
     * @param appId the if of the app you want to get the contract for
     * @returns the Contract
     */
    async getContract(appId) {
        const response = await this.executeQuery(graphql_1.ContractDocument, {
            appId: appId !== null && appId !== void 0 ? appId : this.appId,
        });
        return response.contract;
    }
    /**
     * Deploys the contract of current app
     * @category Contract
     * @param params.name The name of the contract
     * @param params.blockchain The blockchain of the contract
     * @returns the Contract deployed
     */
    async deployContract(params) {
        var _a;
        const response = await this.executeMutation(graphql_1.DeployContractDocument, {
            ...params,
            appId: (_a = params === null || params === void 0 ? void 0 : params.appId) !== null && _a !== void 0 ? _a : this.appId,
        }, ["Contract"]);
        return response.deployContract;
    }
    /**
     * Reserves an NFT listing
     * @category NFTListing
     * @param id ID of the listing
     * @returns the Invoice of the reserve
     */
    async reserveListing(id) {
        const response = await this.executeMutation(graphql_1.ReserveDocument, { id });
        return response.reserve;
    }
    /**
     * Starts the checkout process of an NFT Listing
     * @category NFTListing
     * @param params.invoiceId ID of the invoice created from calling reserveListing
     * @param params.onError Redirect URL to handle error in checkout
     * @param params.onSuccess  Redirect URL to handle successful checkout
     * @returns the Invoice of the reserve
     */
    async checkoutListing(params) {
        const { invoiceId, onError, onSuccess } = params;
        const response = await this.executeMutation(graphql_1.CheckoutDocument, { invoiceId, onError, onSuccess });
        return response.checkout;
    }
    /**
     * Creates an NFT listing
     * @category NFTListing
     * @param params.attributes The metadata for this listing in JSON format
     * @param params.description The description of the listing
     * @param params.nftModelId The ID of the NFT models to list for sale in this NFT listing.
     * @param params.price The price of the NFTlisting. Must be greater than 0.
     * @param params.title The title of the listing
     *
     * @returns the NFTListing created
     */
    async createNFTListing(params) {
        const response = await this.executeMutation(graphql_1.CreateNftListingDocument, { data: params }, ["NftListing", "NftModel"]);
        return response.createNFTListing;
    }
    /**
     * Updates an existing NFTListing.
     * @category NFTListing
     * @param params.id ID of the NFTListing to update
     * @param params.data Data to update NFTListing
     * @returns the updated NFTSet
     */
    async updateNftListing(params) {
        const response = await this.executeMutation(graphql_1.UpdateNftListingDocument, params, ["NftListing", "NftModel"]);
        return response.updateNFTListing;
    }
    /**
     * Fetches an NFT listing
     * @category NFTListing
     * @param id ID of the listing
     * @returns the NFTListing
     */
    async getNFTListing(id) {
        const response = await this.executeQuery(graphql_1.NftListingDocument, { id });
        return response.nftListing;
    }
    /**
     * Fetches NFT listings from given params
     * @category NFTListing
     * @param params.cursor Cursor for pagination
     * @param params.filter Params to filter nft listings
     * @param params.maxResults Max results to return
     * @returns the NFTListingList
     */
    async getNFTListings(params) {
        const response = await this.executeQuery(graphql_1.NftListingDocument, params);
        return response.nftListings;
    }
    /**
     * Fetches the current app user
     * @category User
     * @returns the AppUser
     */
    async getAppUser() {
        const response = await this.executeQuery(graphql_1.AppUserDocument);
        return response.appUser;
    }
    /**
     * fetches the custom transaction to be executed on the blockchain
     * @category Blockchain
     * @param id ID of the custom transaction
     * @returns the CustomTransaction
     */
    async getCustomTransaction(id) {
        const response = await this.executeQuery(graphql_1.CustomTransactionDocument, { customTransactionId: id });
        return response.customTransaction;
    }
    /**
     * fetches the custom transactions to be executed on the blockchain
     * @category Blockchain
     * @param params.search search query for full text search on transaction name
     * @param params.maxResults Max results to return
     * @param params.cursor Cursor for pagination
     * @param params.appId ID of the app to fetch the transactions for, Can only be used with admin credentials
     * @returns the CustomTransactionList
     */
    async getCustomTransactions(params) {
        const response = await this.executeQuery(graphql_1.CustomTransactionsDocument, params);
        return response.customTransactions;
    }
    /**
     * Creates a custom transaction to be executed on the blockchain. This needs to be approved by a Niftory admin before it can be executed.
     * @category Blockchain
     * @param params.data The data to create the custom transaction
     * @param params.appId ID of the app to create the transaction for, Can only be used with admin credentials
     * @returns  the CustomTransaction created
     */
    async createCustomTransaction(params) {
        const response = await this.executeMutation(graphql_1.CreateCustomTransactionDocument, params, ["CustomTransactionList"]);
        return response.createCustomTransaction;
    }
    /**
     * Approves a custom transaction to be executed on the blockchain. Can only be used by a niftory admin.
     * @category Blockchain
     * @param id ID of the custom transaction to approve
     * @returns the CustomTransaction approved
     */
    async approveCustomTransaction(id) {
        const response = await this.executeMutation(graphql_1.ApproveCustomTransactionDocument, { approveCustomTransactionId: id }, [
            "CustomTransactionList",
        ]);
        return response.approveCustomTransaction;
    }
    /**
     * deletes a custom Transaction from the database, This won't delete the transaction from the blockchain. Can only be used by an app admin
     * @category Blockchain
     * @param id ID of the custom transaction to delete
     * @returns the CustomTransaction deleted
     */
    async deleteTransaction(id) {
        const response = await this.executeMutation(graphql_1.DeleteCustomTransactionDocument, { deleteCustomTransactionId: id }, [
            "CustomTransactionList",
        ]);
        return response.deleteCustomTransaction;
    }
    /**
     * Gets a Blockchain transaction
     * @category Blockchain
     * @param params.id ID of the blockchain transaction
     * @param params.hash Tranasction hash of the blockchain transaction
     * @returns the MarketplaceListing
     */
    async getBlockchainTransaction(params) {
        const { id, hash } = params;
        const response = await this.executeQuery(graphql_1.BlockchainTransactionDocument, { id, hash });
        return response.blockchainTransaction;
    }
    /**
     * executes a custom transaction on the blockchain
     * @category Blockchain
     * @param params.transaction The code for the blockchain transaction to execute
     * @param params.walletId The database ID of the wallet to sign on behalf of. One of walletId, userId, or address must be provided.
     * @param params.userId The database ID of the user to sign on behalf of. One of walletId, userId, or address must be provided.
     * @param params.address The address of the wallet to sign on behalf of. One of walletId, userId, or address must be provided.
     * @param params.name A user-friendly name for the transaction.
     * @param param.args Args to be passed to the transaction. These should be in a JSON Object of the form {name: value}.
     * @param params.appId The appId in which the wallet resides. Can only be used with admin credentials.
     */
    async executeBlockchainTransaction(params) {
        var _a;
        const response = await this.executeMutation(graphql_1.ExecuteTransactionDocument, {
            ...params,
            appId: (_a = params === null || params === void 0 ? void 0 : params.appId) !== null && _a !== void 0 ? _a : this.appId,
        }, ["BlockchainTransaction"]);
        return response.executeTransaction;
    }
    /**
     * Initiates checkout with Dapper Wallet of specified NFTModels, and returns a signed transaction to complete checkout with. Flow blockchain only.
     * @category DapperWallet
     * @param params.nftModelId NftModel Id of the NFT
     * @param params.address The address of the wallet to transfer to
     * @param params.expiry The expiry of this checkout as a unix timestamp
     * @param params.price The price in DUC to apply to the checkout
     * @param params.userId Id of user to received the nft
     * @param params.walletId Id of the wallet to transfer to
     * @returns CheckoutWithDapperWalletResponse
     */
    async checkoutWithDapperWallet(params) {
        const response = await this.executeMutation(graphql_1.CheckoutWithDapperWalletDocument, params);
        return response.checkoutWithDapperWallet;
    }
    /**
     * Marks the checkout with Dapper Wallet as complete, and updates the NFT as belonging to specified wallet. Called after checkoutWithDapperWallet once purchase is completed.
     * @category DapperWallet
     * @param params.transactionId The completed Dapper Wallet transaction ID.
     * @param params.nftDatabaseId Database ID of the `NFT`, returned from `checkoutWithDapperWallet`.
     *
     * @returns the CompleteCheckoutWithDapperWalletMutation
     */
    async completeCheckoutWithDapperWallet(params) {
        const response = await this.executeMutation(graphql_1.CompleteCheckoutWithDapperWalletDocument, params);
        return response.completeCheckoutWithDapperWallet;
    }
    /**
     * Signs a transaction for Dapper Wallet.
     * @category DapperWallet
     * @param params.transaction The transaction to sign.
     * @returns signed signature
     */
    async signTransactionForDapperWallet(params) {
        const response = await this.executeMutation(graphql_1.SignTransactionForDapperWalletDocument, params);
        return response.signTransactionForDapperWallet;
    }
    /**
     * Signs a transaction for Niftory Wallet.
     * @category Wallet
     * @param params.transaction The transaction to sign.
     * @param params.address The address of the wallet to sign on behalf of. One of walletId, userId, or address must be provided.
     * @param params.appId The appId in which the wallet resides. Can only be used with admin credentials.
     * @param params.userId The ID of the user to sign on behalf of. One of walletId, userId, or address must be provided.
     * @param params.walletId The database ID of the wallet to sign on behalf of. One of walletId, userId, or address must be provided.
     * @returns signed signature
     */
    async signTransaction(params) {
        const response = await this.executeMutation(graphql_1.SignTransactionDocument, params);
        return response.signTransaction;
    }
    /**
     * Fetches a Marketplace listing from its ID
     * @category Marketplace
     * @param id ID of the listing
     * @returns the MarketplaceListing
     */
    async getMarketplaceListing(id) {
        const response = await this.executeQuery(graphql_1.MarketplaceListingDocument, { id });
        return response.marketplaceListing;
    }
    /**
     * Fetches a Marketplace listings filtered and paginated from given params
     * @category Marketplace
     * @param params.cursor Cursor for pagination
     * @param params.filter Params to filter marketplace listings
     * @param params.maxResults Max results to return
     * @param params?.appId AppId to which the listings belong to, Only for admin users
     * @returns the MarketplaceListing
     */
    async getMarketplaceListings(params) {
        var _a;
        const response = await this.executeQuery(graphql_1.MarketplaceListingsDocument, {
            ...params,
            appId: (_a = params === null || params === void 0 ? void 0 : params.appId) !== null && _a !== void 0 ? _a : this.appId,
        });
        return response.marketplaceListings;
    }
    /**
     * Marks the marketplace listing as completed. This should be caleld after listing transactions is sealed with its trasanctionhash and nftId
     * @category Marketplace
     * @param params.nftId The database id of the NFT
     * @param params.transactionId The sealed blockchain transaction hash.
     *
     * @returns the MarketplaceListing
     */
    async completeMarketplaceList(params) {
        const response = await this.executeMutation(graphql_1.CompleteMarketplaceListDocument, params, ["MarketplaceListing"]);
        return response.completeMarketplaceList;
    }
    /**
     * Marks the marketplace listing as cancelled
     * @category Marketplace
     * @param params.id The database id of the marketplace listings
     * @param params.transactionId The sealed blockchain transaction hash.
     *
     * @returns the MarketplaceListing
     */
    async completeMarketplaceCancel(params) {
        const response = await this.executeMutation(graphql_1.CompleteMarketplaceCancelDocument, params, ["MarketplaceListing"]);
        return response.completeMarketplaceCancel;
    }
    /**
     * Transfers the NFT in the marketplace listing to a new owner
     * @category Marketplace
     * @param params.id The database id of the marketplace listings
     * @param params.transactionId The sealed blockchain transaction hash of purchase.
     *
     * @returns the MarketplaceListing
     */
    async completeMarketplacePurchase(params) {
        const response = await this.executeMutation(graphql_1.CompleteMarketplacePurchaseDocument, params, ["MarketplaceListing"]);
        return response.completeMarketplacePurchase;
    }
    /**
     * Creates an NFTContent object containing pre-signed URLs that can then be used to upload a file and poster. The primary file will automatically be uploaded to IPFS once it's been uploaded to the pre-signed URL. Each returned File has a 'state' property to track the upload.
     * @category Nft
     * @param params.name A friendly name for the file.
     * @param params.contentType The Content-Type (MIME type) of the file to be uploaded. This must match the Content-Type header you will use to upload the file to the returned URL. If this is left empty, your Content-Type header must also be empty.
     * @param params.description Description about the file. If specified, this description will be added to the IPFS file metadata.
     * @param params.posterContentType The Content-Type (MIME type) of the poster file to be uploaded. This must match the Content-Type header you will use to upload the poster to the returned URL. If this is left empty, your Content-Type header must also be empty.
     *
     * @returns the {NftContent}
     */
    async uploadNftContent(params) {
        const response = await this.executeMutation(graphql_1.UploadNftContentDocument, params);
        return response.uploadNFTContent;
    }
    /**
     * Gets the list of invoices for your app.
     * @category Invoice
     * @param params.appId the ID of the app - only required when calling with admin privileges
     * @param params.cursor Cursor for pagination
     * @param params.maxResults Max results to return
     * @param params.filter The params to filter invoices by.
     * @returns the {InvoiceList}
     */
    async getInvoices(params) {
        const response = await this.executeMutation(graphql_1.InvoicesDocument, params);
        return response.invoices;
    }
}
exports.NiftoryClient = NiftoryClient;
